/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import i18n from 'i18next';
import type { UseTranslationResponse } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { CORE_NAMESPACE, coreTranslations } from '@opengeoweb/core';
import {
  FORM_FIELDS_NAMESPACE,
  formFieldsTranslations,
} from '@opengeoweb/form-fields';
import {
  safelyInitI18nForTestsAndStories,
  SHARED_NAMESPACE,
  sharedTranslations,
} from '@opengeoweb/shared';
import sigmetAirmetTranslations from '../../../locales/sigmet-airmet.json';

export const SA_NAMESPACE = 'sigmetairmet';

export const initSigmetAirmetI18n = (): void => {
  safelyInitI18nForTestsAndStories(i18n, SA_NAMESPACE, [
    { ns: SA_NAMESPACE, translations: sigmetAirmetTranslations },
    { ns: SHARED_NAMESPACE, translations: sharedTranslations },
    { ns: CORE_NAMESPACE, translations: coreTranslations },
    { ns: FORM_FIELDS_NAMESPACE, translations: formFieldsTranslations },
  ]);
};

const ns = [SA_NAMESPACE, FORM_FIELDS_NAMESPACE];

export const translateInTestsAndStories = (
  key: string,
  params: Record<string, string | number> | undefined = undefined,
): string => {
  return i18n.t(key, { ns, ...params });
};

export const useSigmetAirmetTranslation = (): UseTranslationResponse<
  typeof SA_NAMESPACE,
  typeof i18n
> => {
  return useTranslation(ns);
};

export { i18n };
